/**
 * GAME KIT — les primitives partagées de `/games` (Tour Arcade).
 *
 * Pourquoi ce fichier : la page Games a grossi (11 modules : 7 jeux/synthés +
 * 4 outils de scène). Recopier dans chaque jeu la barre de score, la lecture du
 * record et l'état de partie aurait produit 11 copies divergentes. Ici, une
 * seule implémentation, un seul contrat DOM (`data-game-bar`, `data-game-score`,
 * `data-game-best`, `data-game-overlay`) : les tests et la QA headless lisent la
 * même chose partout.
 *
 * RÈGLES (elles tiennent tout `/games`) :
 *  - ZÉRO dépendance externe, ZÉRO requête réseau : Canvas 2D + DOM + Web Audio.
 *  - Les records vivent dans `localStorage` (`bandz.games.high.<jeu>`) et ne
 *    baissent JAMAIS : une partie ratée n'efface pas le record.
 *  - Tactile d'abord : tout ce qui se tape fait ≥ 48 px (`game-ctl`).
 *  - Aucun `AudioContext` à l'import ni au montage — uniquement dans un
 *    gestionnaire de geste (iOS/Chrome l'exigent, jsdom n'en a pas).
 */
import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type GameStatus = 'idle' | 'running' | 'paused' | 'over' | 'won';

/** Record lu depuis `localStorage` — un record illisible vaut 0, jamais NaN. */
export function readHighScore(key: string): number {
  try {
    const raw = localStorage.getItem(key);
    const n = raw === null ? 0 : Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch {
    // Navigation privée / quota : le jeu doit rester jouable sans record.
    return 0;
  }
}

/**
 * Meilleur score courant + enregistreur. `bump` n'écrit que si la valeur
 * DÉPASSE le record : un score plus faible ne peut pas écraser le meilleur.
 */
export function useHighScore(key: string): [number, (value: number) => void] {
  const [best, setBest] = useState<number>(() => readHighScore(key));
  const bump = useCallback(
    (value: number) => {
      setBest((prev) => {
        const next = Math.floor(value);
        if (!Number.isFinite(next) || next <= prev) return prev;
        try {
          localStorage.setItem(key, String(next));
        } catch {
          /* record non persistant — pas une raison d'arrêter la partie */
        }
        return next;
      });
    },
    [key],
  );
  return [best, bump];
}

/** Statut de partie : l'état React ET une ref lue par la boucle (sans re-render). */
export function useGameStatus(initial: GameStatus = 'idle') {
  const [status, setStatusState] = useState<GameStatus>(initial);
  const statusRef = useRef<GameStatus>(initial);
  const setStatus = useCallback((next: GameStatus) => {
    statusRef.current = next;
    setStatusState(next);
  }, []);
  return { status, statusRef, setStatus };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

interface ScoreBarProps {
  score: number;
  best: number;
  status: GameStatus;
  onToggle: () => void;
  onRestart: () => void;
  /** Statistique secondaire (niveau, vies, série…) — optionnelle. */
  extra?: string;
}

/** Barre de score Roadcase — une seule ligne, comme les barres de section U2h. */
export function ScoreBar({ score, best, status, onToggle, onRestart, extra }: ScoreBarProps) {
  const { t } = useTranslation();
  const primary =
    status === 'running' ? t('games.pause') : status === 'paused' ? t('games.resume') : t('games.start');
  return (
    <div className="game-bar" data-game-bar="1">
      <div className="game-stat">
        <span className="game-stat-label">{t('games.score')}</span>
        <span className="game-stat-value" data-game-score={score}>
          {score}
        </span>
      </div>
      <div className="game-stat">
        <span className="game-stat-label">{t('games.best')}</span>
        <span className="game-stat-value" data-game-best={best}>
          {best}
        </span>
      </div>
      {extra ? (
        <div className="game-stat game-stat-extra" data-game-extra={extra}>
          <span className="game-stat-label">{extra}</span>
        </div>
      ) : null}
      <div className="game-bar-actions">
        <button type="button" className="btn btn-primary game-btn" onClick={onToggle}>
          {primary}
        </button>
        <button type="button" className="btn game-btn" onClick={onRestart}>
          {t('games.restart')}
        </button>
      </div>
    </div>
  );
}

export function StageOverlay({ tone, children }: { tone: 'info' | 'over' | 'won'; children: ReactNode }) {
  return (
    <div className={`game-overlay game-overlay-${tone}`} data-game-overlay={tone}>
      {children}
    </div>
  );
}
